import React, { useState } from 'react';
import { ThemeContext } from '../../lib/ThemeContext.js';

// Source: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
// Version 4.1
const pSBC = (p, c0, c1, l) => {
    let r,
        g,
        b,
        P,
        f,
        t,
        h,
        m = Math.round,
        a = typeof c1 == 'string';
    if (
        typeof p != 'number' ||
        p < -1 ||
        p > 1 ||
        typeof c0 != 'string' ||
        (c0[0] != 'r' && c0[0] != '#') ||
        (c1 && !a)
    )
        return null;
    (h = c0.length > 9),
        (h = a ? (c1.length > 9 ? true : c1 == 'c' ? !h : false) : h),
        (f = pSBC.pSBCr(c0)),
        (P = p < 0),
        (t =
            c1 && c1 != 'c'
                ? pSBC.pSBCr(c1)
                : P
                  ? { r: 0, g: 0, b: 0, a: -1 }
                  : { r: 255, g: 255, b: 255, a: -1 }),
        (p = P ? p * -1 : p),
        (P = 1 - p);
    if (!f || !t) return null;
    if (l)
        (r = m(P * f.r + p * t.r)),
            (g = m(P * f.g + p * t.g)),
            (b = m(P * f.b + p * t.b));
    else
        (r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
            (g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
            (b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5));
    (a = f.a),
        (t = t.a),
        (f = a >= 0 || t >= 0),
        (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0);
    if (h)
        return (
            'rgb' +
            (f ? 'a(' : '(') +
            r +
            ',' +
            g +
            ',' +
            b +
            (f ? ',' + m(a * 1000) / 1000 : '') +
            ')'
        );
    else
        return (
            '#' +
            (
                4294967296 +
                r * 16777216 +
                g * 65536 +
                b * 256 +
                (f ? m(a * 255) : 0)
            )
                .toString(16)
                .slice(1, f ? undefined : -2)
        );
};

pSBC.pSBCr = d => {
    const i = parseInt;
    let n = d.length,
        x = {};
    if (n > 9) {
        const [r, g, b, a] = (d = d.split(','));
        n = d.length;
        if (n < 3 || n > 4) return null;
        (x.r = i(r[3] == 'a' ? r.slice(5) : r.slice(4))),
            (x.g = i(g)),
            (x.b = i(b)),
            (x.a = a ? parseFloat(a) : -1);
    } else {
        if (n == 8 || n == 6 || n < 4) return null;
        if (n < 6)
            d =
                '#' +
                d[1] +
                d[1] +
                d[2] +
                d[2] +
                d[3] +
                d[3] +
                (n > 4 ? d[4] + d[4] : '');
        d = i(d.slice(1), 16);
        if (n == 9 || n == 5)
            (x.r = (d >> 24) & 255),
                (x.g = (d >> 16) & 255),
                (x.b = (d >> 8) & 255),
                (x.a = Math.round((d & 255) / 0.255) / 1000);
        else
            (x.r = d >> 16),
                (x.g = (d >> 8) & 255),
                (x.b = d & 255),
                (x.a = -1);
    }
    return x;
};

function getTheme(value, options) {
    if (value) {
        return options[value];
    } else {
        return options[Object.keys(options)[0]];
    }
}

const ThemedForm = ({ styles, onlyColor, children }) => {
    const [scope, setScope] = useState(
        `theme-${parseInt(Math.random() * 100000000)}`,
    );

    const inputTheme = getTheme(styles?.input, {
        'rounded-rectangle': 'theme-input-rounded-rectangle',
        square: 'theme-input-square',
        pill: 'theme-input-pill',
    });

    const buttonTheme = getTheme(styles?.button, {
        'rounded-rectangle': 'theme-button-rounded-rectangle',
        'inverse-rounded-rectangle': 'theme-button-inverse-rounded-rectangle',
        square: 'theme-button-square',
        'inverse-square': 'theme-button-inverse-square',
        pill: 'theme-button-pill',
        'inverse-pill': 'theme-button-inverse-pill',
    });

    const fontTheme = getTheme(styles?.font, {
        sans: 'theme-font-sans',
        serif: 'theme-font-serif',
        mono: 'theme-font-mono',
    });

    const color = styles?.color || '#2C87F3';
    const lightColor = pSBC(0.2, color);
    const lightestColor = pSBC(0.7, color);
    const css = `
.${scope} {

    &.theme-button-rounded-rectangle .form-button,
    &.theme-button-square .form-button,
    &.theme-button-pill .form-button,
    .themed-button.form-button
    { background-color: ${color}; &:hover {background-color: ${lightColor}} }

    &.theme-button-rounded-rectangle .form-button-light,
    &.theme-button-square .form-button-light,
    &.theme-button-pill .form-button-light,
    .themed-button.form-button-light
    { background-color: ${lightestColor}; color: ${color} }

    &.theme-button-inverse-rounded-rectangle .form-button,
    &.theme-button-inverse-square .form-button,
    &.theme-button-inverse-pill .form-button,
    .themed-inverse-button.form-button
    { border-color: ${color}; color: ${color}; &:hover {background-color: ${color}; color: #ffffff} }

    &.theme-button-inverse-rounded-rectangle .form-button-light,
    &.theme-button-inverse-square .form-button-light,
    &.theme-button-inverse-pill .form-button-light,
    .themed-inverse-button.form-button-light
    { border-color: ${lightColor}; color: ${lightColor}; &:hover {background-color: ${lightestColor}; color: ${lightColor}} }


    input:focus, select:focus { border-color: ${color} }
    [type='checkbox'], [type='radio'] { color: ${color} }

    a, .link { color: ${color} }
    .link-no-underline { color: ${color} }

    section.choose-block:hover, section.choose-block.selected-block { border-color: ${color}; outline-color: ${color}; color: ${color}; }
    section.choose-block:hover div:last-of-type,
    section.choose-block.selected-block div:last-of-type,
    section.choose-block.choose-block-selected div:last-of-type
        { color: ${color} }

    .availability-picker button { background-color: transparent; color: inherit }
    .time-option:hover { border-color: ${color}; color: ${color} }
    button.time-option-selected { border-color: ${color}; color: ${color} }
    .date-option-selected { color: ${color} !important; background-color: ${lightestColor} !important; }

    .availability-picker .react-calendar__navigation__prev-button,
    .availability-picker .react-calendar__navigation__next-button {
        color: ${color}
    }

    .availability-picker button.react-calendar__tile--active,
    .availability-picker .react-calendar__tile--active:enabled:hover,
    .availability-picker .react-calendar__tile--active:enabled:focus {
        color: ${color};
        background-color: white;
        box-shadow: 0px 0px 0px 2px ${color};
    }

    .event-confirmation-icon { color: ${color} }

    .btn-inverse, .form-button.btn-inverse { background-color: ${lightestColor}; color: ${color} }
    .btn-inverse:hover, .form-button.btn-inverse:hover { background-color: ${lightestColor} }
    .btn-danger, .form-button.btn-danger { background-color: #fee2e2; color: #D25D5D }
    .btn-danger:hover, .form-button.btn-danger:hover { background-color: #fee2e2 }

    .themed-text { color: ${color} }
    .themed-outline { outline-color: ${color} }
    .themed-border { border-color: ${color} }
    .hover-themed-border:hover { border-color: ${color} }
    .themed-bg-light { background-color: ${lightestColor} }
    .themed-bg { background-color: ${color} }

    .thank-you-check-container { background-color: ${lightestColor}; color: ${color} }
}
    `;

    const className = onlyColor
        ? `themed ${scope}`
        : `themed ${scope} ${fontTheme} ${inputTheme} ${buttonTheme}`;

    return (
        <ThemeContext.Provider
            value={{
                color: color,
                styles: styles,
            }}
        >
            <div className={className}>
                <style type="text/css">{css}</style>
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

export default ThemedForm;
